import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { useWishlistSubscribe } from '@jsmdg/wishlist-library';
import {
    Breakpoint,
    NotificationSnackbar,
    SnackbarContextProvider,
    SnackbarPosition,
    useBreakpoint,
} from '@jsmdg/yoshi';
import { PageType } from '../../../shared/enums/pageType';
import { RecommendationType } from '../../../shared/enums/recommendationType';
import {
    type FragmentContext,
    type RecommendationConfig,
    type RecommendationsRootProps,
} from '../../../shared/types';
import { BestsellerSlider } from '../BestsellerSlider';
import { Categories } from '../Categories';
import { LastSeenProductSlider } from '../LastSeenProductSlider';
import { ProductCategorySlider } from '../ProductCategorySlider';
import { RelatedProducts } from '../RelatedProducts';

const Recommendations = (rootComponentProps: RecommendationsRootProps): JSX.Element => {
    const upToXSBreakpoint = useBreakpoint(Breakpoint.XS);
    const { customerState, pageType, recommendationConfigs } = rootComponentProps;
    const { initialWishlist } = useFragmentContext<FragmentContext>();
    const { wishlist } = useWishlistSubscribe(initialWishlist);

    const showLastSeenProductSlider =
        (pageType === PageType.Wishlist && !wishlist?.length) || pageType !== PageType.Wishlist;

    const renderFragment = (
        recommendationConfig: RecommendationConfig,
        key: string,
    ): JSX.Element | null => {
        switch (recommendationConfig.type) {
            case RecommendationType.MainCategories:
                return <Categories key={key} options={recommendationConfig.options} />;
            case RecommendationType.LastSeenProducts:
                return showLastSeenProductSlider ? (
                    <LastSeenProductSlider
                        lazyLoad
                        key={key}
                        options={recommendationConfig.options}
                        wishlist={wishlist}
                    />
                ) : null;
            case RecommendationType.BestSeller:
                return (
                    <BestsellerSlider
                        key={key}
                        lazyLoad
                        recommendationConfig={recommendationConfig}
                    />
                );
            case RecommendationType.Category:
            case RecommendationType.ProductCategory: {
                return <ProductCategorySlider key={key} lazyLoad config={recommendationConfig} />;
            }

            case RecommendationType.RelatedProducts:
                return recommendationConfig.options?.productIds?.length ? (
                    <RelatedProducts
                        key={key}
                        recommendationConfig={recommendationConfig}
                        lazyLoad
                    />
                ) : null;
            default:
                return null;
        }
    };

    return (
        <SnackbarContextProvider>
            <NotificationSnackbar
                className={upToXSBreakpoint ? 'container' : 'px-2x'}
                position={SnackbarPosition.Bottom}
                a11yCloseButtonText=""
            />

            <div
                data-id="recommendations-wrapper"
                className={classNames('w-100 pt-6x pb-sm-6x px-3x px-sm-0', {
                    'pt-md-0': pageType === PageType.Wishlist && customerState.isLoggedIn,
                })}
            >
                {recommendationConfigs.map(
                    (recommendationConfig: RecommendationConfig, index: number) => {
                        const key = `${recommendationConfig.type}_${index}`;
                        return <div key={key}>{renderFragment(recommendationConfig, key)}</div>;
                    },
                )}
            </div>
        </SnackbarContextProvider>
    );
};

export { Recommendations };
